// client/src/pages/AdminReturnRequests.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Select, MenuItem, Chip, Alert, CircularProgress,
} from '@mui/material';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { pending: 'warning', approved: 'info', rejected: 'default', completed: 'success' };
const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'completed'];

const AdminReturnRequests = () => {
  const { token } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRequests = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/return-requests`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch return requests.');
      setRequests(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleStatusChange = async (id, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/return-requests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error();
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      alert('Failed to update return request.');
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Return Requests</Typography>
      {requests.length === 0 ? (
        <EmptyState icon={<AssignmentReturnOutlinedIcon />} title="No return requests" description="Customer return/refund requests will show up here." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell><TableCell>Customer</TableCell><TableCell>Reason</TableCell>
                <TableCell align="right">Order Total</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>#{r.order_id}</TableCell>
                  <TableCell>{r.customer_email}</TableCell>
                  <TableCell>{r.reason}{r.description && ` — ${r.description}`}</TableCell>
                  <TableCell align="right">{formatCurrency(r.total_amount)}</TableCell>
                  <TableCell>
                    <Select
                      size="small" value={r.status}
                      onChange={(e) => handleStatusChange(r.id, e.target.value)}
                      renderValue={(val) => <Chip label={val} size="small" color={statusColor[val] || 'default'} sx={{ textTransform: 'capitalize' }} />}
                    >
                      {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminReturnRequests;
